// EditorContext projection — derives EditorContext from ParsedDocument source.
// PRD: Ralph/SqlGrammar/PRD.md § 2.4. G11.
//
// Pure text-based implementation: no web-tree-sitter dependency.
// Error recovery data comes from ParsedDocument.recovery (populated by the
// normalize/ layer when tree-sitter is available).
//
// The four sub-concerns live in sibling files:
//   editorContextScan.ts    — string / comment position detection
//   editorContextClause.ts  — tokenizer + clause/paren state machine
//   scanPrimitives.ts       — low-level char-scan helpers (shared)

import type { EditorContext, EditorContextPositionKind, SqlClause, TokenKind } from '../model/editorContext';
import type { ParsedDocument, QualifiedName, SqlRange } from '../model';
import { resolveSlotKind } from './editorContextSlot';
import { buildLineIndex, rangeFromOffsets } from './lineIndex';
import {
  isBareIdentChar,
  isBareIdentStart,
  isOperatorChar,
  isWhitespace,
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipSingleQuoted,
  skipToEol,
  tryDollarQuoted,
} from './scanPrimitives';
import { isInsideBlockComment, isInsideLineComment, isInsideStringLiteral } from './editorContextScan';
import { computeClauseContext, findStatementBounds, SQL_KEYWORDS } from './editorContextClause';

// ---------------------------------------------------------------------------
// Dotted-base extraction — backward scan
// ---------------------------------------------------------------------------

type DottedBaseParts = Pick<QualifiedName, 'database' | 'schema' | 'name'>;

function extractDottedBase(
  source: string,
  offset: number,
  stmtStart: number,
): DottedBaseParts | undefined {
  let pos = offset;
  while (pos > stmtStart && isBareIdentChar(source.charCodeAt(pos - 1))) pos -= 1;

  const parts: string[] = [];
  for (;;) {
    let dotPos = pos;
    while (dotPos > stmtStart && isWhitespace(source.charCodeAt(dotPos - 1))) dotPos -= 1;
    if (dotPos === stmtStart || source.charCodeAt(dotPos - 1) !== 0x2e) break;
    let idEnd = dotPos - 1;
    while (idEnd > stmtStart && isWhitespace(source.charCodeAt(idEnd - 1))) idEnd -= 1;
    const ident = consumeIdentBackward(source, idEnd, stmtStart);
    if (!ident) break;
    parts.unshift(ident);
    pos = idEnd - ident.length;
    if (
      source.charCodeAt(pos) === 0x5b ||
      source.charCodeAt(pos) === 0x22 ||
      source.charCodeAt(pos) === 0x60
    ) {
      pos -= 1;
    }
  }

  if (parts.length === 0) return undefined;
  if (parts.length === 1) return { name: parts[0] };
  if (parts.length === 2) return { schema: parts[0], name: parts[1] };
  return { database: parts[0], schema: parts[1], name: parts[2] };
}

function consumeIdentBackward(source: string, end: number, limit: number): string | null {
  if (end <= limit) return null;
  const last = source.charCodeAt(end - 1);
  if (last === 0x5d) {
    let i = end - 2;
    while (i >= limit && source.charCodeAt(i) !== 0x5b) i -= 1;
    return i < limit ? null : source.slice(i + 1, end - 1);
  }
  if (last === 0x22) {
    let i = end - 2;
    while (i >= limit && source.charCodeAt(i) !== 0x22) i -= 1;
    return i < limit ? null : source.slice(i + 1, end - 1);
  }
  if (last === 0x60) {
    let i = end - 2;
    while (i >= limit && source.charCodeAt(i) !== 0x60) i -= 1;
    return i < limit ? null : source.slice(i + 1, end - 1);
  }
  let start = end;
  while (start > limit && isBareIdentChar(source.charCodeAt(start - 1))) start -= 1;
  return start === end ? null : source.slice(start, end);
}

// ---------------------------------------------------------------------------
// Token at offset
// ---------------------------------------------------------------------------

function findTokenAtOffset(
  source: string,
  stmtStart: number,
  stmtEnd: number,
  offset: number,
  engine: string,
  lineIdx: ReturnType<typeof buildLineIndex>,
): { kind: TokenKind; range: SqlRange } | null {
  let i = stmtStart;
  while (i < stmtEnd) {
    const c = source.charCodeAt(i);
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) {
      const end = skipToEol(source, i + 2, stmtEnd);
      if (i < offset && offset < end) return { kind: 'line-comment', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if (engine === 'mysql' && c === 0x23) {
      const end = skipToEol(source, i + 1, stmtEnd);
      if (i < offset && offset < end) return { kind: 'line-comment', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) {
      const end = skipBlockComment(source, i + 2, stmtEnd);
      if (i < offset && offset < end) return { kind: 'block-comment', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) {
      if (i === offset) {
        let end = i + 1;
        while (end < stmtEnd && isWhitespace(source.charCodeAt(end))) end += 1;
        return { kind: 'whitespace', range: rangeFromOffsets(lineIdx, i, end) };
      }
      i += 1; continue;
    }
    if ((c === 0x4e || c === 0x6e) && source.charCodeAt(i + 1) === 0x27) {
      const end = skipSingleQuoted(source, i + 2, stmtEnd);
      if (offset >= i && offset < end) return { kind: 'literal', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if (engine === 'postgres' && c === 0x24) {
      const end = tryDollarQuoted(source, i, stmtEnd);
      if (end !== -1) {
        if (offset >= i && offset < end) return { kind: 'literal', range: rangeFromOffsets(lineIdx, i, end) };
        i = end; continue;
      }
    }
    if (c === 0x27) {
      const end = skipSingleQuoted(source, i + 1, stmtEnd);
      if (offset >= i && offset < end) return { kind: 'literal', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if ((engine === 'mssql' || engine === 'sqlite') && c === 0x5b) {
      const end = skipBracketedId(source, i + 1, stmtEnd);
      if (offset >= i && offset < end) return { kind: 'identifier', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if (engine === 'mysql' && c === 0x60) {
      const end = skipBacktickId(source, i + 1, stmtEnd);
      if (offset >= i && offset < end) return { kind: 'identifier', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if (c === 0x22) {
      const end = skipDoubleQuoted(source, i + 1, stmtEnd);
      if (offset >= i && offset < end) return { kind: 'identifier', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if (c === 0x28 || c === 0x29 || c === 0x2c || c === 0x2e || c === 0x3b) {
      if (i === offset) {
        const kindMap: Record<number, TokenKind> = {
          0x28: 'lparen', 0x29: 'rparen', 0x2c: 'comma', 0x2e: 'dot', 0x3b: 'semicolon',
        };
        return { kind: kindMap[c], range: rangeFromOffsets(lineIdx, i, i + 1) };
      }
      i += 1; continue;
    }
    if (c >= 0x30 && c <= 0x39) {
      let end = i + 1;
      while (end < stmtEnd) {
        const d = source.charCodeAt(end);
        if ((d >= 0x30 && d <= 0x39) || d === 0x2e || d === 0x65 || d === 0x45) end += 1;
        else break;
      }
      if (offset >= i && offset < end) return { kind: 'literal', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    if (isBareIdentStart(c)) {
      let end = i + 1;
      while (end < stmtEnd && isBareIdentChar(source.charCodeAt(end))) end += 1;
      const text = source.slice(i, end);
      if (offset >= i && offset < end) {
        return { kind: SQL_KEYWORDS.has(text.toUpperCase()) ? 'keyword' : 'identifier', range: rangeFromOffsets(lineIdx, i, end) };
      }
      i = end; continue;
    }
    if (isOperatorChar(c)) {
      let end = i + 1;
      while (end < stmtEnd && isOperatorChar(source.charCodeAt(end))) end += 1;
      if (offset >= i && offset < end) return { kind: 'operator', range: rangeFromOffsets(lineIdx, i, end) };
      i = end; continue;
    }
    i += 1;
  }
  return null;
}

// ---------------------------------------------------------------------------
// EditorContextPositionKind resolver
// ---------------------------------------------------------------------------

function resolvePositionKind(
  source: string,
  offset: number,
  engine: string,
  hasDottedBase: boolean,
  tokenKind: TokenKind | null,
  clause: SqlClause | null,
): EditorContextPositionKind {
  if (isInsideLineComment(source, offset)) return 'in-line-comment';
  if (isInsideBlockComment(source, offset)) return 'in-block-comment';
  if (isInsideStringLiteral(source, offset, engine)) return 'in-string-literal';
  if (hasDottedBase) return 'on-dotted-identifier';
  if (tokenKind === 'keyword') return 'on-keyword';
  if (tokenKind === 'identifier') return 'on-identifier';
  // Whitespace / no-token positions — consult the clause for the GRA-7
  // A6 reserved arms. Token-identity wins everywhere else (on-keyword
  // / on-identifier / on-punctuation), since the token shape is the
  // more specific signal.
  if (tokenKind === 'whitespace' || tokenKind === null) {
    if (clause === 'from') return 'after-from';
    if (clause === 'join-on') return 'after-join-on';
    if (clause === 'where' || clause === 'delete-where') return 'after-where';
    return 'on-whitespace';
  }
  if (tokenKind === 'lparen' || tokenKind === 'rparen' || tokenKind === 'comma' || tokenKind === 'dot' || tokenKind === 'semicolon') return 'on-punctuation';
  if (tokenKind === 'line-comment') return 'in-line-comment';
  if (tokenKind === 'block-comment') return 'in-block-comment';
  return 'unknown';
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

const EMPTY_RANGE: SqlRange = {
  startOffset: 0, endOffset: 0, startLine: 0, startColumn: 0, endLine: 0, endColumn: 0,
};

/** Build an EditorContext for `offset` from the raw ParsedDocument. */
export function buildEditorContext(doc: ParsedDocument, offset: number): EditorContext {
  const { source, recovery } = doc;
  const engine = doc.options.engine;
  const n = source.length;
  const clamped = Math.max(0, Math.min(n, offset));

  if (n === 0) {
    return {
      positionInfo: { positionKind: 'on-whitespace', tokenRange: EMPTY_RANGE },
      errorRecoveryView: [],
      tokenAtOffset: null,
      clauseContext: null,
      slotKind: 'none',
      slotSource: 'text',
    };
  }

  const lineIdx = buildLineIndex(source);
  const { start: stmtStart, end: stmtEnd } = findStatementBounds(source, clamped, engine);

  const rawToken = findTokenAtOffset(source, stmtStart, Math.min(stmtEnd + 1, n), clamped, engine, lineIdx);
  const tokenAtOffset = rawToken ? { kind: rawToken.kind as TokenKind, range: rawToken.range } : null;
  const dottedBaseParts = extractDottedBase(source, clamped, stmtStart);
  const tokenRange = rawToken ? rawToken.range : rangeFromOffsets(lineIdx, clamped, clamped);

  // String / comment classification first — those positions get a
  // forced positionKind and skip the clause + reserved-arm work.
  // (GRA-7 A6: clauseContext now feeds resolvePositionKind, so it has
  // to be computed before the call rather than after as before.)
  const inStringOrComment =
    isInsideStringLiteral(source, clamped, engine) ||
    isInsideLineComment(source, clamped) ||
    isInsideBlockComment(source, clamped);

  const clauseContext: EditorContext['clauseContext'] = inStringOrComment
    ? null
    : computeClauseContext(source, stmtStart, clamped, engine);

  const positionKind = resolvePositionKind(
    source,
    clamped,
    engine,
    dottedBaseParts !== undefined,
    rawToken?.kind ?? null,
    clauseContext?.clause ?? null,
  );

  const errorRecoveryView: EditorContext['errorRecoveryView'] = [
    ...recovery.errorRanges.map((r) => ({ range: r, confidence: 1.0 })),
    ...recovery.missingRanges.map((r) => ({ range: r, confidence: 0.5 })),
  ];

  // GRA-8 slot seam: refine ambiguous clause arms only. Never fires inside a
  // string/comment (those positions keep their forced position kind and must
  // not be re-classified — e.g. JSON-path-in-literal short-circuits elsewhere).
  // Today always text-derived; the deferred tree-sitter swap flips slotSource.
  const slotKind: EditorContext['slotKind'] = inStringOrComment
    ? 'none'
    : resolveSlotKind({
        source,
        stmtStart,
        offset: clamped,
        engine,
        clause: clauseContext?.clause ?? null,
      });

  return {
    positionInfo: { positionKind, dottedBase: dottedBaseParts, tokenRange },
    errorRecoveryView,
    tokenAtOffset,
    clauseContext,
    slotKind,
    slotSource: 'text',
  };
}
